import { useMemo } from "react"
import { Filter, SegmentRuleField, SegmentRuleOperator } from "sharedTypes"
import { chipToRule, chipHasValue, ruleValueToChipValues } from "utilities/chipRules"
import useSearchParams from "services/useSearchParams"
import { FORM_QUESTION_FIELD_PREFIX } from "pages/EventShow/Guests/TableView/Filters/config"

type WireRule = {
  field: string,
  operator: SegmentRuleOperator,
  value: string | number | (string | number)[],
}

const VALID_STATIC_FIELDS: ReadonlySet<string> = new Set(Object.values(SegmentRuleField))
const VALID_OPERATORS: ReadonlySet<string> = new Set(
  ["eq", "ne", "gt", "lt", "in", "not_in", "has_all", "not_has_all", "contains", "not_contains"],
)

// Accepts both static enum fields and dynamic form-question fields.
const isValidField = (field: string): boolean => (
  VALID_STATIC_FIELDS.has(field) || field.startsWith(FORM_QUESTION_FIELD_PREFIX)
)

// Routes through chipToRule so the URL wire matches the POST/PUT body.
export const serializeFilters = (filters: Filter[]): string => {
  const wire = filters.filter(chipHasValue).map((f) => chipToRule(f) as WireRule)

  return encodeURIComponent(JSON.stringify(wire))
}

export const useUrlFilters = (): Filter[] => {
  const { rules } = useSearchParams<{ rules?: string }>()

  return useMemo(() => parseFiltersFromUrl(rules), [rules])
}

// `raw` arrives URL-decoded already (URLSearchParams), so no second
// decodeURIComponent — would throw URIError on values containing `%`.
// Unknown fields/operators (stale URLs, hand edits) are dropped so they
// can't ghost-chip into every subsequent push.
export const parseFiltersFromUrl = (raw: string | undefined): Filter[] => {
  if (!raw) return []
  try {
    const decoded = JSON.parse(raw)
    if (!Array.isArray(decoded)) return []

    return decoded.flatMap((rule: WireRule, i: number) => {
      if (!isValidField(rule.field) || !VALID_OPERATORS.has(rule.operator)) return []

      return [{
        id: `url-${i}`,
        field: rule.field,
        operator: rule.operator,
        values: ruleValueToChipValues(rule.value),
      }]
    })
  } catch {
    return []
  }
}
